
import React, { useEffect, useRef, useState } from 'react';
import { useAppContext } from '../../share-components/AppContext';
import { useNavigate } from 'react-router-dom';
import { ROUTE_PRODUCT_DETAILS, ROUTE_PRODUCT_LIST } from '../../constants/routeConstants';
import * as Yup from 'yup';
import { Button, Card } from 'react-bootstrap';
import { isNullOrEmptyString, scrollToFormError } from '../../services/UtilityService';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import AlertDialog from '../../share-components/AlertDialog';
import ConfirmDialog from '../../share-components/ConfirmDialog';
import { createProduct, getActiveCategories } from '../../services/ManagementService';
import FormTitle from '../../share-components/FormTitle';
import { SERVER_ERROR_MESSAGE } from '../../constants/commonConstants';

const ProductCreate = () => {
    const { setLoading, setShowApiErrorMessage, setRouteNaviData } = useAppContext();
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [colorName, setColorName] = useState('');
    const [additionalColorPrice, setAdditionalColorPrice] = useState('');
    const [colorList, setColorList] = useState([]);
    const [size, setSize] = useState('');
    const [additionalSizePrice, setAdditionalSizePrice] = useState('');
    const [sizeList, setSizeList] = useState([]);
    const [formValue, setFormValue] = useState(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [categories, setCategories] = useState([]);
    const [imagePreview, setImagePreview] = useState(null);
    const [createdProductId, setCreatedProductId] = useState(null);
    const [isFirstLoaded, setIsFirstLoaded] = useState(false);
    const fileInputRef = useRef(null);
    const navigate = useNavigate();

    const fetchCategories = async () => {
        setLoading(true);
        try {
            const response = await getActiveCategories();
            let sortedCategories = (response || [])?.sort((a, b) => (a.categoryName || "")?.localeCompare((b?.categoryName || "")));
            setCategories(sortedCategories);
            setIsFirstLoaded(true);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        setRouteNaviData("PRODUCT >> NEW PRODUCT");
        fetchCategories();
    }, [])

    const initialValues = {
        categoryId: '',
        brandName: '',
        productName: '',
        salePrice: '',
        multiColorFlag: false,
        // colorData: '',
        multiSizeFlag: false,
        // sizeData: '',
        productPicture: '',
        notShowInDashboardFlag: false,
        postedLink: '',
        comment: ''
    };

    const validationSchema = Yup.object({
        categoryId: Yup.string().required('Category Name is required.'),
        brandName: Yup.string()
            .max(30, 'Brand Name must not exceed 30 characters.')
            .required('Brand Name is required.'),
        productName: Yup.string()
            .max(30, 'Product Name must not exceed 30 characters.')
            .required('Product Name is required.'),
        salePrice: Yup.number().nullable()
            .transform((value) => (Number.isNaN(value) ? null : value))
            .min(1, 'Sale Price must exceed 0¥.')
            .max(1000000, 'Sale Price must not exceed 1000000¥.'),
        multiColorFlag: Yup.boolean(),
        multiSizeFlag: Yup.boolean(),
        productPicture: Yup.mixed().nullable()
            .test(
                'fileType',
                'Only JPG, PNG, and JPEG files are allowed.',
                (value) => {
                    if (!value) return true;
                    return value && value.type.startsWith('image/');
                }
            ),
            // .test(
            //     'fileSize',
            //     'Image size must be less than 2MB.',
            //     (value) => {
            //         if (!value) return true;
            //         return value && value.size <= 2 * 1024 * 1024; // 2MB size limit
            //     }
            // ),
        notShowInDashboardFlag: Yup.boolean(),
        postedLink: Yup.string().nullable()
            .max(500, 'Link must not exceed 500 characters.'),
        comment: Yup.string().nullable()
            .max(300, 'Comment must not exceed 300 characters.')
    });

    const onSubmit = async (form) => {
        setFormValue(form);
        setShowConfirmDialog(true);
    };

    const handleCreateNewProduct = async () => {
        setShowConfirmDialog(false);
        setLoading(true);
        try {
            const formData = {
                ...formValue,
                categoryId: parseInt(formValue?.categoryId) || 0,
                salePrice: parseInt(formValue?.salePrice) || 0,
                colorList: colorList,
                sizeList: sizeList
            }
            const createdId = await createProduct(formData);
            setCreatedProductId(createdId);
            setShowSuccessDialog(true);
        } catch (error) {
            const errMsg = error?.response?.data?.errorMessage || SERVER_ERROR_MESSAGE;
            if (error.status !== 401 && error.status !== 403) {
                setShowApiErrorMessage(errMsg);
            }
        } finally {
            setLoading(false);
        }
    }

    const handleSuccessDialogClose = () => {
        setShowSuccessDialog(false);
        if ((parseInt(createdProductId) || 0) > 0) {
            navigate(`${ROUTE_PRODUCT_DETAILS}/${createdProductId}`);
        } else {
            navigate(ROUTE_PRODUCT_LIST)
        }
    }

    const handleAddColorClick = (setFieldTouched) => {
        if (isNullOrEmptyString(colorName)) {
            return;
        }
        setColorList(prev => {
            const filteredData = (prev || []).filter(data => data?.color?.trim() !== colorName?.trim());
            const additionalSalePrice = isNaN(parseInt(additionalColorPrice)) || parseInt(additionalColorPrice) === -0 ? 0 : parseInt(additionalColorPrice);
            const updatedData = [
                ...filteredData,
                {
                    color: colorName,
                    additionalSalePrice: additionalSalePrice
                }
            ]
            setColorName("");
            setAdditionalColorPrice("");
            setTimeout(() => {
                setFieldTouched('multiColorFlag');
            }, 100);
            return updatedData;
        });
    }

    const handleAddSizeClick = (setFieldTouched) => {
        if (isNullOrEmptyString(size)) {
            return;
        }
        setSizeList(prev => {
            const filteredData = (prev || []).filter(data => data?.size?.trim() !== size?.trim());
            const additionalSalePrice = isNaN(parseInt(additionalSizePrice)) || parseInt(additionalSizePrice) === -0 ? 0 : parseInt(additionalSizePrice);
            const updatedData = [
                ...filteredData,
                {
                    size: size,
                    additionalSalePrice: additionalSalePrice
                }
            ]
            setSize("");
            setAdditionalSizePrice("");
            setTimeout(() => {
                setFieldTouched('multiSizeFlag');
            }, 100);
            return updatedData;
        });
    }

    const handleImageChange = (e, setFieldValue) => {
        const file = e.target.files[0];
        if (file) {
            setImagePreview(URL.createObjectURL(file));
            setFieldValue('productPicture', file);
        } else {
            setImagePreview(null);
            setFieldValue("productPicture", "");
        }
    };

    return (
        <div id='createProductPage' style={{ paddingBottom: '50px' }}>
            <FormTitle title={"New Product"} isCenter={true} />

            {isFirstLoaded && <>
                <Card style={{ padding: '10px', margin: '0 auto', maxWidth: '650px' }}>
                    <Card.Body className='pt-2'>
                        <div className="container">
                            <div className="row justify-content-center">
                                <div style={{ maxWidth: "550px" }}>
                                    <Formik
                                        initialValues={initialValues}
                                        validationSchema={validationSchema}
                                        validate={(values) => {
                                            const errors = {};
                                            if (values.multiColorFlag && colorList.length <= 1) {
                                                errors.multiColorFlag = 'Add at least two colors.';
                                            }
                                            if (values.multiSizeFlag && sizeList.length <= 1) {
                                                errors.multiSizeFlag = 'Add at least two sizes.';
                                            }
                                            return errors;
                                        }}
                                        onSubmit={onSubmit}>
                                        {({ values, setFieldTouched, errors, isValid, dirty }) => {
                                            const firstError = !dirty ? "Fill the required fields." : Object.values(errors)[0];
                                            return (
                                                <Form>
                                                    <div className="mb-1">
                                                        <label className="custom-label">Category Name*</label>
                                                        <Field as="select" name="categoryId" className="w-100 customInputControl">
                                                            <option value="" disabled>--Select--</option>
                                                            {categories.map(category => (
                                                                <option key={category.categoryId} value={category.categoryId}>{category.categoryName}</option>
                                                            ))}
                                                        </Field>
                                                        <ErrorMessage name="categoryId" component="div" className="formErrDiv text-danger" />
                                                    </div>

                                                    <div className="mb-1">
                                                        <label className="custom-label">Brand Name*</label>
                                                        <Field name="brandName" type="text" className="w-100 customInputControl" autoComplete="off" />
                                                        <ErrorMessage name="brandName" component="div" className="formErrDiv text-danger" />
                                                    </div>

                                                    <div className="mb-1">
                                                        <label className="custom-label">Product Name*</label>
                                                        <Field name="productName" type="text" className="w-100 customInputControl" autoComplete="off" />
                                                        <ErrorMessage name="productName" component="div" className="formErrDiv text-danger" />
                                                    </div>

                                                    <div className="mb-1">
                                                        <label className="custom-label">Sale Price</label>
                                                        <Field name="salePrice" type="number"
                                                            inputMode="numeric"  // forces numeric keypad on mobile
                                                            pattern="[0-9]*"      // enforces only digits
                                                            className="w-100 customInputControl clearNumberSpin"
                                                            onKeyDown={(e) => {
                                                                if (e.key === "e" || e.key === "-" || e.key === "." || e.key === "+") {
                                                                    e.preventDefault(); // Prevent form submission
                                                                }
                                                            }}
                                                            onWheel={(e) => e.target.blur()}
                                                        />
                                                        <ErrorMessage name="salePrice" component="div" className="formErrDiv text-danger" />
                                                    </div>

                                                    <div className="mt-3 form-check form-switch form-check-reverse text-start mb-2">
                                                        <label className="form-check-label custom-label">
                                                            Multiple Colors
                                                            <Field type="checkbox" name="multiColorFlag" className="form-check-input ms-2" />
                                                        </label>
                                                        <ErrorMessage name="multiColorFlag" component="div" className="formErrDiv text-danger" />
                                                    </div>

                                                    {values?.multiColorFlag === true && <div className="mb-1 colorSizeBlockSection">
                                                        {colorList && colorList.length > 0 && <div className='colorSizeBlockWrapper mb-2'>
                                                            {colorList.map((data, indexToRemove) => (
                                                                <span key={indexToRemove} className='colorSizeBlock'>{data.color}{` (${data.additionalSalePrice >= 0 ? '+' : ''}${data.additionalSalePrice}¥)`}
                                                                    <button type='button' className='icon-btn p-0 ms-2 text-danger'
                                                                        onClick={() => {
                                                                            setColorList(prev => prev.filter((_, index) => index !== indexToRemove));
                                                                            setTimeout(() => {
                                                                                setFieldTouched("multiColorFlag")
                                                                            }, 100);
                                                                        }}>
                                                                        <i className="bi bi-x-circle-fill"></i></button>
                                                                </span>
                                                            ))}
                                                        </div>}

                                                        <div className='row mb-1'>
                                                            <div className='col-md-4'>
                                                                <label className="custom-label">Color</label>
                                                            </div>
                                                            <div className='col-md-8'>
                                                                <input value={colorName} type="text" className="w-100 customInputControl" autoComplete="off"
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === "Enter") {
                                                                            e.preventDefault(); // Prevent form submission
                                                                        }
                                                                    }}
                                                                    onChange={(e) => setColorName(e.target.value)} />
                                                            </div>
                                                        </div>
                                                        <div className='row mb-1'>
                                                            <div className='col-md-4'>
                                                                <label className="custom-label">Additional Price</label>
                                                            </div>
                                                            <div className='col-md-8'>
                                                                <input value={additionalColorPrice} type="number"
                                                                    inputMode="numeric"  // forces numeric keypad on mobile
                                                                    pattern="[0-9]*"      // enforces only digits
                                                                    className="w-100 customInputControl clearNumberSpin"
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === "Enter" || e.key === "e" || e.key === "." || e.key === "+") {
                                                                            e.preventDefault(); // Prevent form submission
                                                                        }
                                                                    }}
                                                                    onWheel={(e) => e.target.blur()}
                                                                    onChange={(e) => setAdditionalColorPrice(e.target.value)} />
                                                                <Button type='button' variant='success' className='appBtn mt-2' disabled={isNullOrEmptyString(colorName)} onClick={() => handleAddColorClick(setFieldTouched)} >Add Color</Button>
                                                            </div>
                                                        </div>
                                                    </div>}

                                                    <div className="mt-3 form-check form-switch form-check-reverse text-start mb-2">
                                                        <label className="form-check-label custom-label">
                                                            Multiple Sizes
                                                            <Field type="checkbox" name="multiSizeFlag" className="form-check-input ms-2" />
                                                        </label>
                                                        <ErrorMessage name="multiSizeFlag" component="div" className="formErrDiv text-danger" />
                                                    </div>
                                                    {values?.multiSizeFlag === true && <div className="mb-1 colorSizeBlockSection">

                                                        {sizeList && sizeList.length > 0 && <div className='colorSizeBlockWrapper mb-2'>
                                                            {sizeList.map((data, indexToRemove) => (
                                                                <span key={indexToRemove} className='colorSizeBlock'>{data.size}{` (${data.additionalSalePrice >= 0 ? '+' : ''}${data.additionalSalePrice})`}
                                                                    <button type='button' className='icon-btn p-0 ms-2 text-danger'
                                                                        onClick={() => {
                                                                            setSizeList(prev => prev.filter((_, index) => index !== indexToRemove));
                                                                            setTimeout(() => {
                                                                                setFieldTouched("multiSizeFlag")
                                                                            }, 100);
                                                                        }}>
                                                                        <i className="bi bi-x-circle-fill"></i></button>
                                                                </span>
                                                            ))}
                                                        </div>}

                                                        <div className='row mb-1'>
                                                            <div className='col-md-4'>
                                                                <label className="custom-label">Size</label>
                                                            </div>
                                                            <div className='col-md-8'>
                                                                <input value={size} type="text" className="w-100 customInputControl" autoComplete="off"
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === "Enter") {
                                                                            e.preventDefault(); // Prevent form submission
                                                                        }
                                                                    }}
                                                                    onChange={(e) => setSize(e.target.value)} />
                                                            </div>
                                                        </div>
                                                        <div className='row mb-1'>
                                                            <div className='col-md-4'>
                                                                <label className="custom-label">Additional Price</label>
                                                            </div>
                                                            <div className='col-md-8'>
                                                                <input value={additionalSizePrice} type="number"
                                                                    inputMode="numeric"  // forces numeric keypad on mobile
                                                                    pattern="[0-9]*"      // enforces only digits
                                                                    className="w-100 customInputControl clearNumberSpin"
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === "Enter" || e.key === "e" || e.key === "." || e.key === "+") {
                                                                            e.preventDefault(); // Prevent form submission
                                                                        }
                                                                    }}
                                                                    onWheel={(e) => e.target.blur()}
                                                                    onChange={(e) => setAdditionalSizePrice(e.target.value)} />
                                                                <Button type='button' variant='success' className='appBtn mt-2' disabled={isNullOrEmptyString(size)} onClick={() => handleAddSizeClick(setFieldTouched)} >Add Size</Button>
                                                            </div>
                                                        </div>
                                                    </div>}

                                                    <div className="mb-1">
                                                        <label className="custom-label">Product Picture</label>
                                                        <Field name="productPicture">
                                                            {({ field, form }) => (
                                                                <div>
                                                                    <input
                                                                        ref={fileInputRef}
                                                                        type="file"
                                                                        className="w-100 customInputControl"
                                                                        onChange={(e) => handleImageChange(e, form.setFieldValue)}
                                                                    />
                                                                    {imagePreview && (
                                                                        <div className="mt-2">
                                                                            <img
                                                                                src={imagePreview}
                                                                                alt="Preview"
                                                                                className="img-thumbnail"
                                                                                style={{ width: "var(--productPictureWidth)", height: "100px", objectFit: "cover" }}
                                                                            />
                                                                            <button
                                                                                style={{
                                                                                    color: 'red'
                                                                                }}
                                                                                type="button"
                                                                                className="icon-btn"
                                                                                onClick={() => {
                                                                                    form.setFieldValue('productPicture', ""); // Reset Formik value
                                                                                    setImagePreview(null);
                                                                                    if (fileInputRef.current) {
                                                                                        fileInputRef.current.value = ""; // Clear input field
                                                                                    }
                                                                                }}
                                                                            >
                                                                                <i className="bi bi-x-circle-fill"></i>
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </Field>
                                                        <ErrorMessage name="productPicture" component="div" className="formErrDiv text-danger" />
                                                    </div>

                                                    <div className="mb-1">
                                                        <label className="custom-label">Posted Link</label>
                                                        <Field name="postedLink" component="textarea" rows="3" className="w-100 customInputControl" autoComplete="off" />
                                                        <ErrorMessage name="postedLink" component="div" className="formErrDiv text-danger" />
                                                    </div>

                                                    <div className="mt-2 form-check form-switch form-check-reverse text-start mb-2">
                                                        <label className="form-check-label custom-label">
                                                            Not Show In Dashboard
                                                            <Field type="checkbox" name="notShowInDashboardFlag" className="form-check-input ms-2" />
                                                        </label>
                                                        <ErrorMessage name="notShowInDashboardFlag" component="div" className="formErrDiv text-danger" />
                                                    </div>

                                                    <div className="mb-1">
                                                        <label className="custom-label">Comment</label>
                                                        <Field name="comment" component="textarea" rows="4" className="w-100 customInputControl" autoComplete="off" />
                                                        <ErrorMessage name="comment" component="div" className="formErrDiv text-danger" />
                                                    </div>


                                                    <div className='floating-bottom-btn-group mt-2'>
                                                        <div className='floating-bottom-btn-grid'>
                                                            <Button
                                                                className="w-100"
                                                                type="submit"
                                                                variant="primary"
                                                                // disabled={!(isValid && dirty)}
                                                                onClick={scrollToFormError}
                                                            >
                                                                Save
                                                            </Button>

                                                            {/* <Button
                                                            className="w-100"
                                                            type="button"
                                                            variant="secondary"
                                                            onClick={() => navigate(ROUTE_PRODUCT_LIST)}
                                                        >
                                                            Go to Product List
                                                        </Button> */}
                                                        </div>
                                                    </div>
                                                </Form>
                                            );
                                        }}
                                    </Formik>
                                </div>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
                <ConfirmDialog
                    dialogId='productCreateConfirmDialog'
                    showDialog={showConfirmDialog}
                    // title='Logout Dialog'
                    body='Are you sure to create a new product?'
                    onConfirm={handleCreateNewProduct}
                    onCancel={() => { setFormValue(null); setShowConfirmDialog(false); }}
                />
                <AlertDialog
                    dialogId={'successDialog'}
                    showDialog={showSuccessDialog}
                    body={'New Product has been successfully created.'}
                    onConfirm={handleSuccessDialogClose}
                />
            </>}
        </div>
    );
}

export default ProductCreate;